import { useEffect, useRef, useState } from "react";
import { postData, useGetData } from "../../services/api/api-tools";
import LogoPerception from '../../assets/images/logos/logo-blue-black.png';
import LogoEDF from '../../assets/images/logos/EDF_Logo.png';
import { useSocket } from "../../contexts/SocketContext";
import { useAlert } from "../../contexts/AlertContext";
import { Loader } from "../../components/loading/Loader";
import TatouImage from "./assets/images/tatou.jpg";
import CheckDosimeterGIF from "./assets/images/check_dosimeter.gif";
import {
  ITEMS_IMAGE_TOP_POSITIONS,
  ITEMS_NAMES,
  STEP_FIRST_ANALYSIS,
  STEP_INIT, STEP_NOT_OK,
  STEP_OK, STEP_SECOND_ANALYSIS, STEPS_DATA
} from "../../services/live-interface/edf-interface-service";
import {
  ArrowPathIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon, FaceFrownIcon, FaceSmileIcon, NoSymbolIcon,
  UsersIcon
} from "@heroicons/react/20/solid";
import { ArrowProgressBar } from "../../components/progressbar/ArrowProgressBar";
import { Modal } from "../../components/modals/Modal";
import { useDailyValues } from "../../hooks/useDailyValues";

export const EDFLivePage = () => {

  const { socket, subscribeToEvent, unsubscribeFromEvent } = useSocket();
  const { showAlert } = useAlert();

  const { data: patterns } = useGetData('patterns', `patterns`);

  const [selectedPattern, setSelectedPattern] = useState(null);
  const [isStarted, setIsStarted] = useState(true);

  /* Countdown variables */
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [totalSeconds, setTotalSeconds] = useState(null);
  const intervalRef = useRef(null);
  const isCountingRef = useRef(false);
  const callbackRef = useRef(null);
  const [displayCountdown, setDisplayCountdown] = useState(false);

  /* Variables specific to EDF */
  const [visibleItems, setVisibleItems] = useState(new Array(ITEMS_NAMES.length).fill(false));
  const [itemsConfidences, setItemsConfidences] = useState(new Array(ITEMS_NAMES.length).fill(0));
  const currentStepRef = useRef(STEP_INIT);
  const areAllItemsVisibleRef = useRef(false);
  const isDosimeterCheckedRef = useRef(false);
  const [confirmEnabled, setConfirmEnabled] = useState(true);
  const [isHumanVisible, setIsHumanVisible] = useState(false);
  const [steps, setSteps] = useState(STEPS_DATA);
  const [errorCode, setErrorCode] = useState(null);
  const lastPingDate = useRef(0);


  const lockedData = useRef(false);

  /* Temporary useEffect to automatically use the first pattern */
  useEffect(() => {
    if (!patterns) return;
    if (patterns?.length === 0) return;
    setSelectedPattern(patterns[0]);
  }, [patterns]);

  const startCountdown = (seconds, callback = null, displayCountdown=true) => {
    if (isCountingRef.current) return;

    // Init
    setRemainingSeconds(seconds);
    setTotalSeconds(seconds);
    setDisplayCountdown(displayCountdown);
    callbackRef.current = callback;
    isCountingRef.current = true;

    let current = seconds;

    intervalRef.current = setInterval(() => {
      current -= 1;
      setRemainingSeconds(current);

      if (current <= 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        isCountingRef.current = false;
        setRemainingSeconds(null);

        if (typeof callbackRef.current === 'function') {
          const cb = callbackRef.current;
          callbackRef.current = null;
          cb();
        }
      }
    }, 1000);
  };

  const cancelCountdown = () => {
    if (!isCountingRef.current) return;

    clearInterval(intervalRef.current);
    intervalRef.current = null;
    callbackRef.current = null;
    isCountingRef.current = false;
    setRemainingSeconds(null);
  };

  const validateVisualStep = (maxIndex) => {
    setSteps(prev => prev.map((item, stepIndex) =>
      stepIndex < maxIndex ?
        { ...item, status: 'complete' } :
      stepIndex === maxIndex ?
        { ...item, status: 'current' } : item
    ));
  };

  useEffect(() => {
    return () => clearInterval(intervalRef.current);
  }, []);

  const [dailyValues, setDailyValues, todayKey, yesterdayKey] = useDailyValues();

  useEffect(() => {
    if (!isStarted) return;
    if (!socket) return;

    const handleReceivedData = (data) => {
      if (lockedData.current) return;

      // Error codes (camera checker)
      if (data?.camera_state !== undefined && data?.camera_state !== null) {
        lastPingDate.current = Date.now();
        setErrorCode(data?.camera_state);
        return;
      }

      // If no human, reset instantly
      if (data?.human_visible === false) {
        handleSuccessIHMReset();
        return;
      }

      // Lock on "OK" / "Not OK" screen
      if (currentStepRef.current === STEP_OK || currentStepRef.current === STEP_NOT_OK) return;

      // Aux variables
      setVisibleItems(visibleItems => data?.visible_items?.map((val, i) => val || visibleItems[i]));
      setItemsConfidences(data?.items_conf);
      setIsHumanVisible(data?.human_visible);
      areAllItemsVisibleRef.current = data?.visible_items?.every(item => item);
      isDosimeterCheckedRef.current = data?.check_dosi_active || isDosimeterCheckedRef.current;

      // If all items are visible, validate instantly and go to second analysis
      if (areAllItemsVisibleRef.current && currentStepRef.current !== STEP_SECOND_ANALYSIS) {
        cancelCountdown();

        // Dosimeter has already been checked before
        if (isDosimeterCheckedRef.current) {
          currentStepRef.current = STEP_OK;
          setDailyValues('total_successes', (dailyValues[todayKey]?.total_successes || 0) + 1);
          setDailyValues('current_series', (dailyValues[todayKey]?.current_series || 0) + 1);
          validateVisualStep(4);
        }
        else{
          currentStepRef.current = STEP_SECOND_ANALYSIS;
          validateVisualStep(1);

          startCountdown(3, () => {
            currentStepRef.current = STEP_NOT_OK;
            setDailyValues('current_series', 0);
            startCountdown(5, () => {handleSuccessIHMReset();});
          });
        }
      }

      // First detection > Go to first analysis
      else if (currentStepRef.current === STEP_INIT && data?.human_visible) {
        console.log('from init to first analysis');
        currentStepRef.current = STEP_FIRST_ANALYSIS;
        validateVisualStep(0);
        setConfirmEnabled(true);

        // During the first analysis
        startCountdown(3, () => {
          currentStepRef.current = STEP_NOT_OK;
          setDailyValues('current_series', 0);
          startCountdown(5, () => {handleSuccessIHMReset();});
        });
      }

      // Second analysis
      else if (currentStepRef.current === STEP_SECOND_ANALYSIS && data?.check_dosi_active) {
        cancelCountdown();
        currentStepRef.current = STEP_OK;
        setDailyValues('total_successes', (dailyValues[todayKey]?.total_successes || 0) + 1);
        setDailyValues('current_series', (dailyValues[todayKey]?.current_series || 0) + 1);
        validateVisualStep(4);
      }
    };

    const handleSuccessIHMReset = () => {
      cancelCountdown();
      setVisibleItems(new Array(ITEMS_NAMES.length).fill(false));
      setIsHumanVisible(false);
      setSteps(STEPS_DATA);
      areAllItemsVisibleRef.current = false;
      isDosimeterCheckedRef.current = false;
      currentStepRef.current = STEP_INIT;
    };

    subscribeToEvent('current_data', handleReceivedData);
    subscribeToEvent('success-ihm-reset', handleSuccessIHMReset);

    return () => {
      unsubscribeFromEvent('current_data', handleReceivedData);
    };
  }, [socket, isStarted, dailyValues]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (lastPingDate.current < Date.now() - 60 * 1000) {
        setErrorCode(255);
      }
    }, 60000); // 60 000 ms = 1 minute

    return () => clearInterval(interval);
  }, []);

  const handleRaiseError = (isError) => {
    console.log('New error raised.');
    postData('live_errors', {
      date: Math.floor(Date.now() / 1000),
      pattern: `/api/patterns/${selectedPattern?.id}`,
      is_error: isError
    });
    showAlert('success', 'Merci pour votre retour !');
    setConfirmEnabled(false);
  };

  /* Stats computation */
  const yesterdayDelta = dailyValues[todayKey]?.total_successes && dailyValues[yesterdayKey]?.total_successes ?
    Math.floor(
      (dailyValues[todayKey].total_successes - dailyValues[yesterdayKey].total_successes) / dailyValues[yesterdayKey].total_successes * 100
    ) : 0;
  const currentSeries = dailyValues[todayKey]?.current_series || 0;
  const totalSuccessesToday = dailyValues[todayKey]?.total_successes || 0;

  return (
    <div className='h-full overflow-hidden bg-perception-black-500'>
      <Modal isOpen={errorCode > 0} setIsOpen={() => null} size="large">
        <h1 className="text-3xl text-center">Oops! Quelque chose s'est mal passé.</h1>
        <div className="flex justify-center m-8">
          <FaceFrownIcon className="w-44 h-44 text-red-200"/>
        </div>
        <p className="text-center">
          Une erreur technique est survenue ou le flux vidéo a été coupé (Code erreur: #{errorCode}).<br/>
          Cliquez sur le bouton ci-dessous pour recharger la page.
        </p>
        <div className="flex justify-center">
          <button className="bg-perception-blue text-white py-2 px-4 rounded-md flex items-center mt-4" onClick={() => window.location.reload()}>
            <ArrowPathIcon aria-hidden="true" className="size-5 mr-2" /> Recharger
          </button>
        </div>
      </Modal>

      <div className='flex flex-col items-center justify-center w-full h-full'>
        {
          isStarted && selectedPattern !== null ?
            <div className="w-full h-full flex items-center text-white text-3xl">
              <div className="bg-perception-black-800 w-1/6 p-4 h-full flex flex-col justify-around">
                <div className="h-48 text-center flex flex-col justify-center items-center text-perception-blue">
                  <span className="text-5xl pb-2">{totalSuccessesToday}</span>
                  <span className="text-xl">Nombre de T'as tout conformes de la journée</span>
                </div>
                {/*<div className="h-48 text-center flex flex-col justify-center items-center text-perception-success-300">*/}
                {/*  <span className="text-5xl pb-2">{yesterdayDelta > 0 ? `+${yesterdayDelta}` : yesterdayDelta}%</span>*/}
                {/*  <span className="text-xl">Evolution du nombre d'entrées VS J-1</span>*/}
                {/*</div>*/}
                <div className="h-48 text-center flex flex-col justify-center items-center text-perception-warn-500">
                  <span className="text-5xl pb-2">{currentSeries}</span>
                  <span className="text-xl">Série de T'as tout conformes de la journée</span>
                </div>
              </div>

              {/* Main part */}
              <div className="w-5/6 h-full flex justify-center p-4 gap-4">
                {/* Left column */}
                <div className="w-1/2 h-full flex flex-col gap-4">
                  {currentStepRef.current === STEP_SECOND_ANALYSIS ||
                    (currentStepRef.current === STEP_NOT_OK && areAllItemsVisibleRef.current && !isDosimeterCheckedRef.current) ?
                    <div className="h-full flex justify-center items-center">
                      <img src={CheckDosimeterGIF} alt="check_dosimeter_gif" className="rounded-md h-full"/>
                    </div>
                    :
                    <div className="h-full flex flex-1 min-h-0 relative">
                      {/* Image */}
                      <img src={TatouImage} alt="tatou_image"
                           className={`h-full w-auto rounded-md ${currentStepRef.current === STEP_SECOND_ANALYSIS ? 'hidden' : ''}`}/>

                      <div className="relative w-full">
                        {
                          isHumanVisible && visibleItems.map((_, index) => {
                            const margin = visibleItems[index] ? 0.2 : 0; /* Not used anymore (display alignment) */
                            return (
                              <div key={index} className="absolute left-0 ml-2 text-5xl" style={{ top: ITEMS_IMAGE_TOP_POSITIONS[index] + margin + '%'}}>
                                {
                                  visibleItems[index] ?
                                    <div className="flex items-end">
                                      <CheckBadgeIcon aria-hidden="true" className="size-12 mr-2 text-perception-success-300" />
                                      &nbsp; {/* Used for display alignment */}
                                    </div>
                                  : isHumanVisible && !visibleItems[index] ?
                                    <div className="flex items-end">
                                      <ExclamationTriangleIcon aria-hidden="true" className="size-10 mr-2 text-yellow-500" />
                                      {ITEMS_NAMES[index]}
                                    </div>
                                  : null
                                }
                              </div>
                            )
                          })
                        }
                      </div>
                    </div>
                  }
                </div>

                {/* Right column */}
                <div className="w-1/2 h-full flex flex-col gap-4">
                  <ArrowProgressBar steps={steps}/>

                  {/* Main part */}
                  <div className="w-full h-full flex justify-center items-center text-center bg-perception-gray-800 rounded-md relative overflow-auto">
                    <style>{`
                      @keyframes bounceScale {
                        0%, 100% {
                          transform: scale(1);
                        }
                        50% {
                          transform: scale(1.1);
                        }
                      }
                    `}</style>
                    {
                      currentStepRef.current === STEP_INIT ?
                        <div className="text-6xl flex flex-col justify-center items-center gap-16 text-center p-8">
                          Veuillez marquer l'arrêt sur le marquage

                          <UsersIcon className="animate-[bounceScale_3s_ease-in-out_infinite] w-40 h-40 text-perception-gray-700"/>
                        </div>
                      : currentStepRef.current === STEP_FIRST_ANALYSIS || currentStepRef.current === STEP_NOT_OK ?
                        <div className="text-left text-7xl p-8">
                          <p className="text-7xl mb-8 text-center">
                            {
                              areAllItemsVisibleRef.current ?
                                <>J'ai un doute sur l'activation du dosimètre</> :
                                <>J'ai un doute sur certains équipements</>
                            }
                          </p>
                          {
                            currentStepRef.current === STEP_NOT_OK &&
                              <div className="flex justify-around mt-32">
                                <button disabled={!confirmEnabled} className={`${confirmEnabled ? 'bg-perception-success-300' : 'bg-perception-gray-500'} text-4xl text-white p-4 rounded-md flex items-center`} onClick={() => handleRaiseError(false)}>
                                  <CheckBadgeIcon aria-hidden="true" className="size-8 mr-2" /> Tu avais raison
                                </button>
                                <button disabled={!confirmEnabled} className={`${confirmEnabled ? 'bg-perception-error-500' : 'bg-perception-gray-500'} text-4xl text-white p-4 rounded-md flex items-center`} onClick={() => handleRaiseError(true)}>
                                  <NoSymbolIcon aria-hidden="true" className="size-8 mr-2" /> Tu avais tort
                                </button>
                              </div>
                          }
                        </div>
                      : currentStepRef.current === STEP_SECOND_ANALYSIS ?
                        <div className="text-7xl text-center">Veuillez vérifier l'activation de votre dosimètre</div>
                      : currentStepRef.current === STEP_OK ?
                        <div className="text-7xl flex flex-col justify-center items-center gap-16 text-center">
                          Entrée en zone conforme !
                          <FaceSmileIcon className="animate-[bounceScale_3s_ease-in-out_infinite] w-44 h-44 text-perception-success-300"/>
                          <div className="text-4xl">
                            N'oubliez pas de prendre un radiamètre<br/>par équipe de travail.
                          </div>
                        </div>
                      : null
                    }
                    <div className="absolute top-8 right-8">
                      <CircleCountdown remainingSeconds={remainingSeconds} totalSeconds={totalSeconds} display={displayCountdown}/>
                    </div>
                  </div>

                  {/* Header name - Project title */}
                  <div className="bg-perception-black-800 p-2 relative rounded-md flex flex-col gap-2 justify-center items-center text-4xl text-center">
                    T'as tout fIAbilisé

                    <div className="flex justify-center items-center text-xl gap-4 text-black">
                      <img src={LogoPerception} alt="logo-perception" className="max-w-[200px] max-h-[30px]" />
                      x
                      <img src={LogoEDF} alt="logo-EDF" className="max-w-[200px] max-h-[30px]" />
                    </div>
                  </div>

                  {/* Logos */}
                </div>
              </div>
            </div>
            :
            <>
              <Loader category="transparent"/>
            </>
        }
      </div>
    </div>
  );
};


const CircleCountdown = ({ remainingSeconds, totalSeconds, display }) => {
  if (!display || remainingSeconds === null || remainingSeconds === undefined || totalSeconds === undefined || totalSeconds === null)
    return null;

  const percentage = remainingSeconds / totalSeconds * 100;

  return (
    <div className="size-28">
      <svg className="size-full -rotate-90" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
        <circle cx="18" cy="18" r="16" fill="none" className="stroke-current text-perception-black-500 dark:text-neutral-700" strokeWidth="2"/>
        <circle cx="18" cy="18" r="16" fill="none" className="stroke-current text-perception-blue dark:text-blue-500"
                strokeWidth="3" strokeDasharray="100" strokeDashoffset={100 - percentage} strokeLinecap="round"/>
      </svg>

      <div className="absolute top-1/2 start-1/2 transform -translate-y-1/2 -translate-x-1/2">
        <span className="text-center text-4xl text-perception-blue">{remainingSeconds}</span>
      </div>
    </div>
  );
};